import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Snackbar,
  Stack,
  SwipeableDrawer,
  Tabs,
  Tab,
  IconButton,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import SellOutlined from "@mui/icons-material/SellOutlined";
import EditRounded from "@mui/icons-material/EditRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";

import type { Bot, BrokerAccount } from "@/data/mock-data";
import { deriveBotReportMetrics } from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";
import { Sparkline } from "@/components/Sparkline";
import { EmptyState } from "@/components/EmptyState";
import { BacktestChart } from "@/components/BacktestChart";
import { syntheticBacktestCurve } from "@/lib/backtest-chart";
import { BotTradeSheet } from "@/components/BotTradeSheet";
import type { TradeMode } from "@/components/BotTradeSheet";
import { BotActionsSheet } from "@/components/BotActionsSheet";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";
import { useCanShareBot } from "@/lib/features";
import { useBotActionsStore } from "@/store/bot-actions-store";

// ---- Public component -----------------------------------------------------

interface BotDetailDrawerProps {
  /** When non-null, drawer is open showing details for this bot. */
  bot: Bot | null;
  /**
   * The cohort the row was tapped in — used for derived `allocPct` and
   * `contribPct` so the drawer's allocation/contribution figures match
   * what the user just saw in the list.
   */
  cohort: Bot[];
  /** Live + paper accounts so we can resolve `bot.accountId → name`.
   *  `readonly` because callers commonly hand in a frozen / aggregated
   *  list (e.g. `liveAccounts` from AccountsPageV2). The drawer never
   *  mutates this array. */
  accounts?: readonly BrokerAccount[];
  /**
   * `true` when the host view is the aggregate "All Live Accounts" tab.
   * (Account row is now always shown — Sergey 2026-04-26 — but the flag
   * is still useful to surface the masked broker id only when the user
   * doesn't already know which account they're looking at.)
   */
  showAccount?: boolean;
  /**
   * Optional Mon-Fri delta keyed by bot id (same shape BotsSimpleView
   * gets). Used as the header fallback when today's daily change isn't
   * available — keeps the drawer in sync with the row's "Today / This
   * week" rule (Sergey 2026-04-27).
   */
  weeklyDeltas?: Record<string, { fraction: number; delta: number }>;
  /**
   * Per-bot overlay (active bots). The drawer's chart uses
   * `overlay.fullCurve` (full lifetime equity curve from
   * reconstruction) and `overlay.chartBaseline` (allocated amount) so
   * the range selector can offer 3M / YTD / 1Y / ALL on active bots
   * the same way the archived drawer already does. Sergey 2026-05-01.
   */
  botOverlays?: Record<
    string,
    {
      fullCurve?: ReadonlyArray<{ date: string; equity: number }>;
      chartBaseline?: number;
    }
  >;
  /**
   * Per-archived-bot full reconstructed equity curve (oldest first).
   * Keyed by bot id. When the visible bot is archived AND has an
   * entry here, the drawer renders the full lifetime chart in place
   * of the 90-day sparkline. Sergey 2026-04-30: archived bots show
   * the whole graph and final-day total in the drawer.
   */
  archivedCurves?: Record<
    string,
    readonly { date: string; equity: number; twrPct: number }[]
  >;
  /**
   * Per-archived-bot allocation override events (loaded from
   * IndexedDB). Each entry is `{ events: [{ date, deltaUsd }] }`
   * representing user-supplied allocation changes over time. The
   * drawer renders these as an editable list when the visible bot is
   * archived. Sergey 2026-04-30 — initial use case: bot 070403cf had
   * its allocation reduced by $4,320 on 2026-02-18.
   */
  archivedOverrides?: Record<string, { events: AllocationEvent[] }>;
  /**
   * Called when the user adds, edits, or deletes an allocation event
   * in the drawer. The parent persists to IndexedDB and re-runs the
   * reconstruction, which flows back to this drawer via updated
   * `bot` + `archivedCurves` props.
   */
  onArchivedOverrideChange?: (
    botId: string,
    ovr: { events: AllocationEvent[] }
  ) => void;
  onClose: () => void;
}

/** Re-exported so callers don't have to import from @/lib/whb-data
 *  separately. Mirrors the type defined there. */
export interface AllocationEvent {
  date: string;
  deltaUsd: number;
}

/**
 * Tabs in the bot detail drawer (Sergey 2026-05-13). "preview" is
 * the rebalance Trade
 * Preview; "structure" is the IF/ELSE rule tree.
 */
type DrawerTab = "live" | "preview" | "backtest" | "structure" | "about";

const DRAWER_TABS: ReadonlyArray<{ key: DrawerTab; label: string }> = [
  { key: "live", label: "Live" },
  { key: "preview", label: "Trade Preview" },
  { key: "backtest", label: "Backtest" },
  { key: "structure", label: "Algo" },
  { key: "about", label: "About" }
];

/** Live-tab sub-tabs — Graph / Holdings / History. */
type LiveSub = "graph" | "holdings" | "history";

const LIVE_SUBS: ReadonlyArray<{ key: LiveSub; label: string }> = [
  { key: "graph", label: "Graph" },
  { key: "holdings", label: "Holdings" },
  { key: "history", label: "History" }
];

/**
 * Bottom-sheet drawer for a single bot — mirrors `OrderDetailDrawer`
 * (Sergey 2026-04-26 strip-down). The simple-list row only shows
 * Today / Total return $ / TWR%; everything else lives here.
 *
 * Layout:
 *   • Header echo — bot icon + name + status, with current value +
 *     today-change top-right. Same identity the user just tapped.
 *   • Sparkline — the equity curve shape, full-bleed. Helps the user
 *     orient inside the drawer ("yes, this is the bot I tapped").
 *   • Detail grid — every metric the simple row dropped (alloc,
 *     contribution, age, TWR, APY, Sharpe, Max DD, net deposits,
 *     account, tickers, last trade, etc.) plus a subset of identity
 *     fields. Two-column auto-grid, label / value.
 *   • Footer — "View bot page" CTA → `/bots/:id` + Close.
 */
export function BotDetailDrawer({
  bot,
  cohort,
  accounts,
  showAccount,
  weeklyDeltas,
  botOverlays,
  archivedCurves,
  archivedOverrides,
  onArchivedOverrideChange,
  onClose
}: BotDetailDrawerProps) {
  // Hold the last non-null bot so the drawer keeps rendering content
  // through its slide-out animation. Without this, the body would clear
  // the moment we set `bot = null` and the user would see a flash of
  // empty drawer for ~200ms. Same trick OrderDetailDrawer uses.
  const [snapshot, setSnapshot] = useState<Bot | null>(bot);
  if (bot && bot !== snapshot) {
    setSnapshot(bot);
  }

  // Active tab in the tab strip. Resets to "live"
  // whenever a new bot opens so the user always lands on the chart.
  const [tab, setTab] = useState<DrawerTab>("live");
  const [liveSub, setLiveSub] = useState<LiveSub>("graph");
  const [openedId, setOpenedId] = useState<string | null>(bot?.id ?? null);
  if (bot && bot.id !== openedId) {
    setOpenedId(bot.id);
    setTab("live");
    setLiveSub("graph");
  }
  // Phase 6 action state — Buy/Sell trade sheet, ⋯ actions sheet, and
  // a transient toast for action acknowledgements. `tradeMode` is kept
  // separate from `tradeOpen` so the sheet doesn't visibly flip
  // Sell→Buy during its slide-out animation.
  const [tradeMode, setTradeMode] = useState<TradeMode>("buy");
  const [tradeOpen, setTradeOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  // `watch: true` adds a "Go to My Watch List" link to the toast.
  const [toast, setToast] = useState<{ msg: string; watch?: boolean } | null>(
    null
  );
  const canShare = useCanShareBot();
  const navigate = useNavigate();

  const watchedIds = useBotActionsStore((s) => s.watchedIds);
  const skipNextIds = useBotActionsStore((s) => s.skipNextIds);
  const toggleWatched = useBotActionsStore((s) => s.toggleWatched);
  const toggleSkipNext = useBotActionsStore((s) => s.toggleSkipNext);

  const visible = snapshot;
  if (!visible) return null;

  // Archived bots are deleted upstream; draft bots have no deployed
  // capital — neither can place a Buy/Sell/Go-to-cash order.
  const tradingDisabled = Boolean(visible.archived || visible.isDraft);
  const isWatched = watchedIds.includes(visible.id);
  const isSkipping = skipNextIds.includes(visible.id);

  // Always look up the owning account regardless of `showAccount` —
  // the Account row is now always rendered. The flag still controls
  // whether we surface the masked broker id alongside the name (only
  // useful in aggregate when the user doesn't already know it).
  const account = accounts?.find((a) => a.id === visible.accountId);

  // Universal "Today / This week / —" fallback chain — same logic the
  // BotsSimpleView row uses (Sergey 2026-04-27, drawer parity):
  //
  //   1. dayChangePct finite & non-zero → "Today"
  //   2. weeklyDeltas[id] provided      → "This week"
  //   3. neither                         → "—"
  //
  // Real bots on weekends/holidays land on (2) since the data layer
  // (useRealBotDetailsBulk) suppresses dayChangeFraction off-trading
  // days but still publishes weeklyChangeDelta.
  const hasToday =
    Number.isFinite(visible.dayChangePct) && visible.dayChangePct !== 0;
  const weeklyDelta = weeklyDeltas?.[visible.id];
  const periodMode: "today" | "week" | "none" = hasToday
    ? "today"
    : weeklyDelta
      ? "week"
      : "none";
  const periodLabel = periodMode === "week" ? "this week" : "today";
  const periodFraction =
    periodMode === "today"
      ? visible.dayChangePct
      : periodMode === "week"
        ? weeklyDelta!.fraction
        : 0;
  const periodDelta =
    periodMode === "today"
      ? visible.dayChangePct * visible.currentValue
      : periodMode === "week"
        ? weeklyDelta!.delta
        : 0;
  const periodPositive = periodFraction >= 0;

  // Derived metrics computed off the same cohort the list rendered, so
  // allocPct and contribPct match the list precisely. ageDays here is
  // also stable across renders (fixed `now` baked into the helper).
  const derived = deriveBotReportMetrics(cohort);
  const d = derived.get(visible.id);
  const allocPct = d?.allocPct ?? 0;
  const contribPct = d?.contribPct ?? 0;
  const ageDays = d?.ageDays ?? 0;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={bot !== null}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          // FIXED height (not max-height): the drawer must stay put as
          // the user switches tabs — a content-driven height made the
          // top edge jump up/down between a tall tab (Live) and a
          // short one (Trade Preview). At 92vh the page's view-tab
          // strip stays peeking above the drawer. Sergey 2026-05-13.
          height: "92vh",
          // Flex column so the header + tab strip + action bar stay
          // fixed while only the active tab's content scrolls.
          display: "flex",
          flexDirection: "column",
          ...desktopBottomDrawerPaperSx,
        },
      }}
    >
      {/* Grab handle — same as OrderDetailDrawer */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Explicit close (×) — circular button, top-right.
       *  Swipe-down + backdrop-tap still work, but a visible × is the
       *  affordance most users reach for, so it's required for
       *  usability (Sergey 2026-05-13). Positioned absolute relative
       *  to the drawer Paper (fixed → establishes the containing
       *  block); zIndex keeps it above the tab strip. */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(t: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(t.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(t.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      {/* Header — icon + name on top, value on its own line below.
       *  Stacking lets the name take the full width and wrap to two
       *  lines for long bot names like "Live S Martingale 2.0 5.23.25"
       *  instead of getting truncated to "Live S…" (Sergey 2026-04-26). */}
      <Stack
        direction="column"
        spacing={1}
        sx={{ px: 2, pt: 1, pb: 1.5 }}
      >
        {/* `pr` clears the absolutely-positioned close (×) button so a
         *  long name or the Paused/Archived chip never slides under it. */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 5 }}>
          <BotAvatar status={visible.status} />
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              lineHeight: 1.2,
              // Allow wrapping — long bot names should stay readable.
              whiteSpace: "normal",
              wordBreak: "break-word"
            }}
          >
            {visible.name}
          </Typography>
          {visible.archived ? (
            <Chip
              label={
                visible.archivedAt
                  ? `Archived · ${formatArchivedDate(visible.archivedAt)}`
                  : "Archived"
              }
              size="small"
              sx={(t) => ({
                bgcolor: alpha(t.palette.text.secondary, 0.15),
                color: t.palette.text.secondary,
                fontWeight: 600,
                height: 22,
                "& .MuiChip-label": { px: 1.25, fontSize: 11 }
              })}
            />
          ) : visible.status === "PAUSED" ? (
            <Chip
              label="Paused"
              size="small"
              sx={(t) => ({
                bgcolor: alpha(t.palette.warning.main, 0.15),
                color: t.palette.warning.main,
                fontWeight: 600,
                height: 22,
                "& .MuiChip-label": { px: 1.25, fontSize: 11 }
              })}
            />
          ) : null}
        </Stack>
        {/* Archived banner — gray informational strip below the title
         *  row when the bot has been deleted from the platform. All
         *  values shown in the drawer are the last known state from
         *  the day before deletion. Sergey 2026-04-30. */}
        {visible.archived && visible.archivedAt && (
          <Box
            sx={(t) => ({
              mt: 1,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: alpha(t.palette.text.secondary, 0.08),
              border: `1px solid ${alpha(t.palette.text.secondary, 0.2)}`,
              display: "flex",
              flexDirection: "column",
              gap: 0.25
            })}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: "text.secondary" }}>
              Archived bot
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
              Last seen on {formatArchivedDateLong(visible.archivedAt)}. Every
              metric and balance below is frozen at that day — the bot has
              been deleted and won't update further.
            </Typography>
          </Box>
        )}
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFeatureSettings: '"tnum" 1',
              lineHeight: 1.1
            }}
          >
            {formatUSD(visible.currentValue)}
          </Typography>
          {visible.archived && visible.archivedAt ? (
            // Archived bots have no "today" — the value above is the
            // last-day equity, frozen at deletion. Show the deletion
            // date inline in the same slot the daily-change indicator
            // would otherwise occupy. Sergey 2026-04-30.
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
            >
              on {formatArchivedDate(visible.archivedAt)}
            </Typography>
          ) : periodMode === "none" ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
            >
              — today
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={(t) => ({
                color: periodPositive
                  ? t.palette.success.main
                  : t.palette.error.main,
                fontWeight: 600,
                fontFeatureSettings: '"tnum" 1',
                whiteSpace: "nowrap"
              })}
            >
              {periodPositive ? "▲" : "▼"}{" "}
              {formatUSD(Math.abs(periodDelta))}{" "}
              ({Math.abs(periodFraction * 100).toFixed(2)}%) {periodLabel}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Tab strip — scrollable so all five fit on a narrow phone. */}
      <Tabs
        value={tab}
        onChange={(_, v: DrawerTab) => setTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 42,
          flexShrink: 0,
          "& .MuiTab-root": {
            minHeight: 42,
            minWidth: "auto",
            px: 1.5,
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none"
          }
        }}
      >
        {DRAWER_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {/* Scrollable tab-content area — the only part of the drawer
       *  that scrolls; header + tabs + action bar stay pinned. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {tab === "live" && (
      <>
      <DrawerSubTabs value={liveSub} onChange={setLiveSub} options={LIVE_SUBS} />
      {liveSub === "graph" && (
      <>
      {/* Sparkline / archived-bot lifetime chart.
       *
       *  Active bots: 90-day mark-to-market equity sparkline at 64dp,
       *  captioned "Past 3 months". Engine: equity-reconstruction.ts
       *  SPARK_LEN = 90. The "Past 3 months" + "3M" chip matches the
       *  convention used by TradingView / Robinhood when a
       *  chart is locked to a single time-window with no scrubber.
       *
       *  Archived bots (Sergey 2026-04-30): when a full lifetime curve
       *  is available via `archivedCurves[bot.id]`, render the WHOLE
       *  inception-to-deletion shape at 140dp instead — for a deleted
       *  bot the user wants to see the full arc, not just the last 90
       *  days. Caption changes to "Lifetime" with a "All" chip.
       *
       *  Either way: skipped entirely when there's no curve to draw
       *  (cache-miss + reconstruction in flight) — an empty panel
       *  reads as a broken layout. */}
      <BotChartSection
        visible={visible}
        archivedCurve={
          visible.archived ? archivedCurves?.[visible.id] : undefined
        }
        activeCurve={
          !visible.archived ? botOverlays?.[visible.id]?.fullCurve : undefined
        }
      />

      {/* Detail grid */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            // `minmax(0, 1fr)` for the value column hard-caps it to
            // the available row space — without the explicit 0 min,
            // CSS Grid lets the 1fr track grow to fit nowrap content,
            // which then pushes the whole drawer past the viewport
            // (Sergey 2026-04-27 reported the right column getting
            // clipped). With `minmax(0,1fr)`, long values wrap or
            // ellipsise inside the column instead.
            gridTemplateColumns: "auto minmax(0, 1fr)",
            rowGap: 0.75,
            columnGap: 2,
            alignItems: "center"
          }}
        >
          {/* Account — always shown so the user sees which broker
           *  account this bot is tied to (matches old WHB UI).
           *  Aggregate view also surfaces the masked id; in single-
           *  account view we suppress it because the user already
           *  knows which account they're inside. */}
          {account && (
            <>
              <DetailLabel>Account</DetailLabel>
              <DetailValue>
                {account.name}
                {showAccount && account.masked && (
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", ml: 0.75 }}
                  >
                    {account.masked}
                  </Box>
                )}
              </DetailValue>
            </>
          )}

          <DetailLabel>Status</DetailLabel>
          <DetailValue>
            <Box
              component="span"
              sx={(t) => ({
                color:
                  visible.status === "ACTIVE"
                    ? t.palette.success.main
                    : t.palette.warning.main,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                fontSize: "0.875rem"
              })}
            >
              {visible.status}
            </Box>
          </DetailValue>

          {/* Net Deposits — Sergey 2026-05-13: WHB's "Allocated" and
           *  "Net Deposits" are the same figure for a bot, so the two
           *  rows were consolidated into one labelled
           *  "Net Deposits". The "% of cohort" annotation is retained. */}
          <DetailLabel>Net Deposits</DetailLabel>
          <DetailValue>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {formatUSD(visible.allocatedAmount)}
            </Box>{" "}
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                display: "inline-block"
              }}
            >
              ({(allocPct * 100).toFixed(1)}% of cohort)
            </Box>
          </DetailValue>

          {/* Bot buying power — label matches the old WHB UI string
           *  ("Bot buying power $5,870.32") so users moving between the
           *  two clients see the same vocabulary. Sourced from
           *  /bots/{id}.portfolio.buyingPower for real bots. */}
          <DetailLabel>Bot buying power</DetailLabel>
          <DetailValue>{formatUSD(visible.buyingPower)}</DetailValue>

          <DetailLabel>Total return</DetailLabel>
          <DetailValue>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              <SignedDollar amount={visible.allTimeGain} />
            </Box>{" "}
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                display: "inline-block"
              }}
            >
              ({formatPctSigned(visible.allTimeGainPct)})
            </Box>
          </DetailValue>

          <DetailLabel>TWR</DetailLabel>
          <DetailValue>{formatPctSigned(visible.twrPct)}</DetailValue>

          <DetailLabel>APY</DetailLabel>
          <DetailValue>{formatPctSigned(visible.apyPct)}</DetailValue>

          <DetailLabel>Sharpe</DetailLabel>
          <DetailValue>
            {Number.isFinite(visible.sharpe)
              ? visible.sharpe.toFixed(2)
              : "—"}
          </DetailValue>

          <DetailLabel>Max drawdown</DetailLabel>
          <DetailValue>
            <Box
              component="span"
              sx={(t) => ({
                color:
                  visible.maxDDPct < 0
                    ? t.palette.error.main
                    : t.palette.text.primary
              })}
            >
              {formatPctSigned(visible.maxDDPct)}
            </Box>
          </DetailValue>

          <DetailLabel>Contribution</DetailLabel>
          <DetailValue>{formatPctSigned(contribPct)}</DetailValue>

          <DetailLabel>Age</DetailLabel>
          <DetailValue>{formatAge(ageDays)}</DetailValue>

          <DetailLabel>Created</DetailLabel>
          <DetailValue>{formatDate(visible.createdAt)}</DetailValue>

          {visible.lastTradeDate && (
            <>
              <DetailLabel>Last trade</DetailLabel>
              <DetailValue>{formatDate(visible.lastTradeDate)}</DetailValue>
            </>
          )}

          {visible.tickers.length > 0 && (
            <>
              <DetailLabel>Universe</DetailLabel>
              <DetailValue>
                {/* Show first 6 symbols + "+ N more" suffix. Allowed
                 *  to wrap to a second line on narrow viewports —
                 *  forcing a single line was clipping the row past the
                 *  viewport. Full list is in the title tooltip for
                 *  hover/long-press reveal. (Sergey 2026-04-27.) */}
                <Box
                  component="span"
                  title={visible.tickers.join(", ")}
                >
                  {visible.tickers.slice(0, 6).join(", ")}
                  {visible.tickers.length > 6 && (
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", ml: 0.5 }}
                    >
                      + {visible.tickers.length - 6} more
                    </Box>
                  )}
                </Box>
              </DetailValue>
            </>
          )}

          {visible.description && (
            <>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>{visible.description}</DetailValue>
            </>
          )}
        </Box>

        {/* Allocation events editor — archived bots only. The default
         *  reconstruction assumes ND = peakSpent for the entire bot
         *  lifetime (matches the no-margin rule), but real users can
         *  bump or reduce allocation mid-stream. This list lets the
         *  user record those changes so the curve and metrics
         *  correct. Sergey 2026-04-30: bot 070403cf needs a Feb 18
         *  −$4,320 event to reflect his March allocation reduction.
         *  Hidden for active bots. */}
        {visible.archived && onArchivedOverrideChange && (
          <ArchivedAllocationEventsEditor
            botId={visible.id}
            inceptionDate={visible.createdAt}
            seedAllocated={
              // Show the INITIAL pre-event allocation (day-0 deposit)
              // as the read-only first row. `visible.allocatedAmount`
              // already reflects post-event ND; back-compute by
              // subtracting the sum of event deltas to recover the
              // original seed.
              visible.allocatedAmount -
              (archivedOverrides?.[visible.id]?.events ?? []).reduce(
                (s, e) => s + e.deltaUsd,
                0
              )
            }
            events={archivedOverrides?.[visible.id]?.events ?? []}
            onChange={(events) =>
              onArchivedOverrideChange(visible.id, { events })
            }
          />
        )}
      </Box>
      </>
      )}
      {liveSub === "holdings" && <BotHoldingsTable bot={visible} />}
      {liveSub === "history" && <BotHistoryTab bot={visible} />}
      </>
      )}

      {tab === "preview" && <BotTradePreviewTab bot={visible} />}
      {tab === "backtest" && <BotBacktestTab bot={visible} />}
      {tab === "structure" && <BotStructureTab />}
      {tab === "about" && (
        <BotAboutTab bot={visible} account={account} />
      )}
      </Box>{/* /scroll area */}

      <Divider />

      {/* Action bar — Buy / Sell / Edit / ⋯ for owned bots. Buy + Sell
       *  open the trade sheet; the ⋯ button opens the actions sheet.
       *  Trading is disabled for archived / draft bots (Phase 6). */}
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="stretch">
          <Button
            variant="contained"
            color="primary"
            disabled={tradingDisabled}
            startIcon={<AttachMoneyRounded sx={{ fontSize: 18 }} />}
            onClick={() => {
              setTradeMode("buy");
              setTradeOpen(true);
            }}
            sx={{ flex: 1, fontWeight: 700 }}
          >
            Buy
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={tradingDisabled}
            startIcon={<SellOutlined sx={{ fontSize: 18 }} />}
            onClick={() => {
              setTradeMode("sell");
              setTradeOpen(true);
            }}
            sx={{ flex: 1, fontWeight: 700 }}
          >
            Sell
          </Button>
          <IconButton
            aria-label="Edit algo"
            onClick={() => setTab("structure")}
            sx={(t) => ({
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 1.5
            })}
          >
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            aria-label="More actions"
            onClick={() => setActionsOpen(true)}
            sx={(t) => ({
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 1.5
            })}
          >
            <MoreHorizRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Buy / Sell trade sheet */}
      <BotTradeSheet
        open={tradeOpen}
        mode={tradeMode}
        bot={visible}
        accountName={account?.name ?? "Account"}
        onClose={() => setTradeOpen(false)}
        onSubmitted={(msg) => setToast({ msg })}
      />

      {/* ⋯ actions sheet */}
      <BotActionsSheet
        open={actionsOpen}
        bot={visible}
        canShare={canShare}
        isWatched={isWatched}
        isSkipping={isSkipping}
        tradingDisabled={tradingDisabled}
        onClose={() => setActionsOpen(false)}
        onGoToCash={() =>
          setToast({ msg: `${visible.name} will move fully to cash.` })
        }
        onToggleSkip={() => {
          toggleSkipNext(visible.id);
          setToast({
            msg: isSkipping
              ? `${visible.name} will trade on its next rebalance.`
              : `${visible.name} will skip its next rebalance.`
          });
        }}
        onEditStructure={() => setTab("structure")}
        onToggleWatch={() => {
          toggleWatched(visible.id);
          setToast(
            isWatched
              ? { msg: `Stopped watching ${visible.name}.` }
              : {
                  msg: `Now watching "${visible.name}" in Marketplace.`,
                  watch: true
                }
          );
        }}
        onCopyToDrafts={() =>
          setToast({ msg: `Saved a draft copy of ${visible.name}.` })
        }
        onToast={(msg) => setToast({ msg })}
      />

      {/* Action acknowledgement toast. Watch toasts carry a "Go to My
       *  Watch List" link to the Marketplace watch segment. */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.watch ? 7000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: (t) => t.zIndex.modal + 6 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ width: "100%" }}
          action={
            toast?.watch ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setToast(null);
                  onClose();
                  navigate("/marketplace?view=watch");
                }}
                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Go to My Watch List
              </Button>
            ) : undefined
          }
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </SwipeableDrawer>
  );
}

// ---- Internals -----------------------------------------------------------

function DetailLabel({ children }: { children: React.ReactNode }) {
  // body1 (1rem) so detail labels match the "Buying Power" scale that
  // Sergey froze as the canonical clickable-text size for the page —
  // same convention as OrderDetailPanel.
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        pr: 2,
        alignSelf: "center"
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.primary",
        textAlign: "right",
        fontFeatureSettings: '"tnum" 1',
        wordBreak: "break-word"
      }}
    >
      {children}
    </Typography>
  );
}

/** Signed dollar amount, green if positive, red if negative, muted at zero. */
function SignedDollar({ amount }: { amount: number }) {
  const positive = amount > 0;
  const negative = amount < 0;
  return (
    <Box
      component="span"
      sx={(t: Theme) => ({
        color: positive
          ? t.palette.success.main
          : negative
            ? t.palette.error.main
            : t.palette.text.primary,
        fontWeight: 700,
        fontFeatureSettings: '"tnum" 1'
      })}
    >
      {positive ? "+" : negative ? "−" : ""}
      {formatUSD(Math.abs(amount))}
    </Box>
  );
}

/** Format a decimal (0.113) as a signed percentage ("+11.30%", "0.00%"). */
function formatPctSigned(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n * 100).toFixed(2)}%`;
}

function formatAge(days: number): string {
  // Defensive: NaN/Infinity/negative slips would render as "NaN years"
  // before. Normalise to "—" so the row stays informative even when
  // the upstream date parse fails.
  if (!Number.isFinite(days) || days < 0) return "—";
  if (days < 7) return `${days} day${days === 1 ? "" : "s"}`;
  if (days < 60) {
    const w = Math.floor(days / 7);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} month${m === 1 ? "" : "s"}`;
  }
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return months > 0
    ? `${years}y ${months}mo`
    : `${years} year${years === 1 ? "" : "s"}`;
}

/**
 * Bot drawer chart block — caption + range chip + Sparkline.
 *
 * Renders the per-bot lifetime / period chart, with underwater
 * shading (green above the baseline, red below). For archived bots
 * the curve is the chain-linked TWR%; for active bots it's the
 * mark-to-market $ equity sparkline from the overlay.
 *
 * The range chip is interactive — tapping cycles through
 * 3M → YTD → 1Y → ALL → 3M and the caption / sliced data update
 * accordingly. Sergey 2026-05-01. For active bots the chip stays
 * static at 3M because the overlay only carries 90 days; longer
 * ranges would surface zeros.
 */
function BotChartSection({
  visible,
  archivedCurve,
  activeCurve
}: {
  visible: Bot;
  archivedCurve?: readonly { date: string; equity: number; twrPct: number }[];
  /** Full daily equity curve for an active bot. Same shape as the
   *  archived curve minus `twrPct` — the drawer plots `equity`
   *  directly. The Sparkline's underwater baseline is now rebased
   *  per-period to the slice's first value (see `baseline` below),
   *  so the chart shows "above/below where you were at the start of
   *  this window" rather than a static reference. */
  activeCurve?: readonly { date: string; equity: number }[];
}) {
  const useArchived = !!archivedCurve && archivedCurve.length > 1;
  // Active bots use their reconstructed full curve when available
  // (Sergey 2026-05-01); otherwise fall back to the 60-day sparkline
  // that's always populated from the broker's states[] feed.
  const useActiveFull = !useArchived && !!activeCurve && activeCurve.length > 1;
  type RangeKey = "3M" | "YTD" | "1Y" | "ALL";
  const [range, setRange] = useState<RangeKey>(
    useArchived ? "ALL" : useActiveFull ? "ALL" : "3M"
  );
  // Reset to a sensible default when switching between bots.
  useMemo(() => {
    setRange(useArchived ? "ALL" : useActiveFull ? "ALL" : "3M");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.id]);

  // Slice the curve to the active range. archivedCurve uses twrPct;
  // activeCurve uses equity; fallback uses the 60-day sparkline numbers
  // straight through (no date stamps, so no range cycling possible).
  const slicedValues = useMemo<number[]>(() => {
    const today = new Date();
    const cutoffFor = (r: RangeKey): string => {
      if (r === "ALL") return "0000-01-01";
      let cutoff: Date;
      if (r === "3M") cutoff = new Date(today.getTime() - 90 * 86400e3);
      else if (r === "YTD") cutoff = new Date(today.getFullYear(), 0, 1);
      else cutoff = new Date(today.getTime() - 365 * 86400e3);
      return cutoff.toISOString().slice(0, 10);
    };
    if (useArchived && archivedCurve) {
      const cutoffIso = cutoffFor(range);
      return archivedCurve
        .filter((d) => d.date >= cutoffIso)
        .map((d) => d.twrPct);
    }
    if (useActiveFull && activeCurve) {
      const cutoffIso = cutoffFor(range);
      return activeCurve
        .filter((d) => d.date >= cutoffIso)
        .map((d) => d.equity);
    }
    return visible.sparkline;
  }, [useArchived, archivedCurve, useActiveFull, activeCurve, range, visible.sparkline]);

  if (slicedValues.length === 0) return null;

  // Caption — describes the range in human terms. ALL surfaces the
  // bot's inception date so the user sees how far back the chart
  // reaches without having to read the X-axis.
  let caption: string;
  if (range === "3M") caption = "Past 3 months";
  else if (range === "YTD") caption = `YTD ${new Date().getFullYear()}`;
  else if (range === "1Y") caption = "Past year";
  else {
    // ALL — show the creation date if we have it, so the user knows
    // exactly when the curve starts. Falls back to a generic
    // "Lifetime" label when createdAt isn't populated.
    const created = visible.createdAt;
    if (created) {
      const d = new Date(
        created.length === 10 ? `${created}T00:00:00Z` : created
      );
      caption = Number.isNaN(d.getTime())
        ? "Lifetime"
        : `Created ${d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
          })}`;
    } else {
      caption = "Lifetime";
    }
  }

  // Baseline rebases to the FIRST value of the sliced period —
  // Sergey 2026-05-01: "underwater" should be relative to where the
  // bot stood at the start of the selected range, not the original
  // allocation. For YTD on an actively-growing bot, baseline is the
  // Jan 1 equity (so above = grew during the year, below = lost
  // during the year). For ALL, the first reconstructed day's equity
  // equals the allocated amount anyway, so the rebase reduces to the
  // previous behaviour. For archived bots' TWR%, the first sliced
  // day's TWR captures the running cumulative return at the start of
  // the window, and the rebase isolates the period's incremental
  // performance.
  const baseline = slicedValues.length > 0 ? slicedValues[0]! : 0;

  // Cycle range on tap. Available when we have a date-stamped curve
  // (archived OR active with fullCurve from the overlay). Active bots
  // without an overlay fall back to the fixed 60-day sparkline and
  // the chip stays static.
  const canCycle = useArchived || useActiveFull;
  const ranges: readonly RangeKey[] = ["3M", "YTD", "1Y", "ALL"];
  const onChipClick = canCycle
    ? () => {
        const i = ranges.indexOf(range);
        setRange(ranges[(i + 1) % ranges.length]!);
      }
    : undefined;

  const height = useArchived || useActiveFull ? 140 : 64;

  return (
    <>
      <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {caption}
          </Typography>
          <Typography
            variant="caption"
            onClick={onChipClick}
            sx={(theme) => ({
              color: "text.secondary",
              fontWeight: 600,
              letterSpacing: 0.4,
              px: 0.75,
              py: 0.125,
              borderRadius: 0.75,
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              cursor: onChipClick ? "pointer" : "default",
              userSelect: "none",
              "&:hover": onChipClick
                ? { bgcolor: alpha(theme.palette.text.primary, 0.12) }
                : undefined
            })}
          >
            {range}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ px: 0.5, pb: 1.5 }}>
        <Sparkline
          values={slicedValues}
          height={height}
          baseline={baseline}
        />
      </Box>
      <Divider />
    </>
  );
}

function formatDate(iso: string): string {
  // Accepts "yyyy-mm-dd" or full ISO. Locale-formatted "Apr 26, 2026"
  // with the user's locale; falls back to the raw string for malformed
  // input so the panel never just shows a blank cell.
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return iso;
  }
}

/**
 * WHB-native bot mark — line-art robot icon, no background fill,
 * matching the BotIcon used in the list (Sergey 2026-04-26). Visual
 * continuity from list to drawer without the coloured-square chrome.
 */
/** Compact "MMM D" date for the inline Archived chip. */
function formatArchivedDate(iso: string): string {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}

/** Long "Mon, MMM D, YYYY" date for the drawer's banner — gives the
 *  user enough context that the Archived chip's compact "MMM D" is
 *  unambiguous. */
function formatArchivedDateLong(iso: string): string {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

/**
 * Editable list of allocation events for an archived bot. Each event
 * is a `(date, deltaUsd)` pair representing a user-driven change to
 * the bot's allocation: positive = bumped (deposit), negative =
 * reduced (withdrawal). The implicit day-0 "deposit" of `seedAllocated`
 * at `inceptionDate` is shown as a read-only first row so the user
 * sees the full allocation history. New events go into the parent's
 * IndexedDB-backed override store via `onChange`. Sergey 2026-04-30.
 */
function ArchivedAllocationEventsEditor({
  botId: _botId,
  inceptionDate,
  seedAllocated,
  events,
  onChange
}: {
  botId: string;
  inceptionDate: string;
  seedAllocated: number;
  events: AllocationEvent[];
  onChange: (events: AllocationEvent[]) => void;
}) {
  const [draftDate, setDraftDate] = useState("");
  const [draftAmount, setDraftAmount] = useState("");
  const [draftSign, setDraftSign] = useState<"deposit" | "withdrawal">(
    "withdrawal"
  );

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => a.date.localeCompare(b.date)),
    [events]
  );

  const addEvent = () => {
    const amt = parseFloat(draftAmount);
    if (!draftDate || !Number.isFinite(amt) || amt <= 0) return;
    const delta = draftSign === "deposit" ? amt : -amt;
    const next = [...events, { date: draftDate, deltaUsd: delta }];
    onChange(next);
    setDraftDate("");
    setDraftAmount("");
  };

  const removeEvent = (idx: number) => {
    const next = events.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.2,
            color: "text.primary"
          }}
        >
          Allocation history
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary" }}
        >
          Edits update the chart + metrics
        </Typography>
      </Stack>

      <Stack
        spacing={0.75}
        sx={{
          mb: 1.25,
          fontFeatureSettings: '"tnum" 1'
        }}
      >
        {/* Implicit day-0 deposit — always shown, read-only. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ color: "text.secondary" }}
        >
          <Typography variant="body2">
            {inceptionDate
              ? new Date(inceptionDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })
              : "Inception"}
            {" · Initial allocation"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            +{formatUSD(Math.max(0, seedAllocated))}
          </Typography>
        </Stack>

        {sortedEvents.map((evt, _idx) => {
          // Find the original index in the unsorted `events` array so
          // the delete handler removes the right entry. Date+amount is
          // a stable enough key for this; identical events on the same
          // day with the same delta are functionally indistinguishable.
          const origIdx = events.findIndex(
            (e) => e.date === evt.date && e.deltaUsd === evt.deltaUsd
          );
          const positive = evt.deltaUsd >= 0;
          return (
            <Stack
              key={`${evt.date}-${evt.deltaUsd}-${origIdx}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">
                {new Date(evt.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                {" · "}
                <Box
                  component="span"
                  sx={{ color: "text.secondary" }}
                >
                  {positive ? "Deposit" : "Withdrawal"}
                </Box>
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="body2"
                  sx={(t) => ({
                    color: positive
                      ? t.palette.success.main
                      : t.palette.error.main,
                    fontWeight: 600
                  })}
                >
                  {positive ? "+" : "−"}
                  {formatUSD(Math.abs(evt.deltaUsd))}
                </Typography>
                <Button
                  size="small"
                  onClick={() => removeEvent(origIdx)}
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    color: "text.secondary",
                    fontWeight: 500
                  }}
                >
                  Remove
                </Button>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      {/* Add-event row — inline date / amount / type selector. Kept
       *  flat (no card / no border) so it doesn't compete with the
       *  metrics grid above. Submit on Enter inside either input. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          flexWrap: "wrap",
          rowGap: 1
        }}
      >
        <Box
          component="input"
          type="date"
          value={draftDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraftDate(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            e.key === "Enter" && addEvent()
          }
          sx={(t) => ({
            font: "inherit",
            color: "text.primary",
            bgcolor: alpha(t.palette.text.primary, 0.04),
            border: "none",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            minWidth: 0,
            flex: "1 1 130px",
            "&:focus": { outline: `1px solid ${t.palette.primary.main}` }
          })}
        />
        <Box
          component="select"
          value={draftSign}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setDraftSign(e.target.value as "deposit" | "withdrawal")
          }
          sx={(t) => ({
            font: "inherit",
            color: "text.primary",
            bgcolor: alpha(t.palette.text.primary, 0.04),
            border: "none",
            borderRadius: 1,
            px: 1,
            py: 0.5
          })}
        >
          <Box component="option" value="withdrawal">−</Box>
          <Box component="option" value="deposit">+</Box>
        </Box>
        <Box
          component="input"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="USD"
          value={draftAmount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraftAmount(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            e.key === "Enter" && addEvent()
          }
          sx={(t) => ({
            font: "inherit",
            color: "text.primary",
            bgcolor: alpha(t.palette.text.primary, 0.04),
            border: "none",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            minWidth: 0,
            flex: "1 1 100px"
          })}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={addEvent}
          disabled={!draftDate || !draftAmount}
          sx={{ flexShrink: 0, fontWeight: 600 }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}

function BotAvatar({
  status
}: {
  status: Bot["status"];
}) {
  const muted = status === "PAUSED";
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: muted ? "text.disabled" : "text.primary"
      }}
    >
      <SmartToyOutlined sx={{ fontSize: 32 }} />
    </Box>
  );
}

// ---- About tab ------------------------------------------------------------

/**
 * Read-only "About" tab — bot identity, description, owning account,
 * status, inception, and the traded universe. Created-by / OOS-start /
 * categories don't exist on the WHB Bot model yet — they'll be added
 * with the bot-builder data model (Phase 5).
 */
function BotAboutTab({
  bot,
  account
}: {
  bot: Bot;
  account?: BrokerAccount;
}) {
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          rowGap: 1,
          columnGap: 2,
          alignItems: "center"
        }}
      >
        <DetailLabel>Name</DetailLabel>
        <DetailValue>{bot.name}</DetailValue>

        {bot.description && (
          <>
            <DetailLabel>Description</DetailLabel>
            <DetailValue>{bot.description}</DetailValue>
          </>
        )}

        {account && (
          <>
            <DetailLabel>Account</DetailLabel>
            <DetailValue>{account.name}</DetailValue>
          </>
        )}

        <DetailLabel>Status</DetailLabel>
        <DetailValue>
          {bot.isDraft ? "Draft" : bot.archived ? "Archived" : bot.status}
        </DetailValue>

        <DetailLabel>Created</DetailLabel>
        <DetailValue>{formatDate(bot.createdAt)}</DetailValue>

        {bot.tickers.length > 0 && (
          <>
            <DetailLabel>Universe</DetailLabel>
            <DetailValue>{bot.tickers.join(", ")}</DetailValue>
          </>
        )}
      </Box>
    </Box>
  );
}

// ---- Backtest tab ---------------------------------------------------------

/** Backtest sub-tabs — Graph / Simulated Returns /
 *  Comparisons / Holdings. */
type BacktestSub = "graph" | "returns" | "comparisons" | "holdings";

const BACKTEST_SUBS: ReadonlyArray<{ key: BacktestSub; label: string }> = [
  { key: "graph", label: "Graph" },
  { key: "returns", label: "Returns" },
  { key: "comparisons", label: "Comparisons" },
  { key: "holdings", label: "Holdings" }
];

/**
 * Backtest tab — Graph / Simulated Returns / Comparisons / Holdings
 * sub-tabs. Numbers are sourced from the Bot
 * model's simulated metrics; the SPY benchmark is a deterministic
 * mock until a real backtest engine is wired.
 */
function BotBacktestTab({ bot }: { bot: Bot }) {
  const [sub, setSub] = useState<BacktestSub>("graph");
  return (
    <>
      <DrawerSubTabs value={sub} onChange={setSub} options={BACKTEST_SUBS} />
      {sub === "graph" && <BotBacktestGraph bot={bot} />}
      {sub === "returns" && <BotSimulatedReturns bot={bot} />}
      {sub === "comparisons" && <BotBacktestComparisons bot={bot} />}
      {sub === "holdings" && <BotHoldingsTable bot={bot} simulated />}
    </>
  );
}

/**
 * Backtest equity-curve graph. Renders the full account chart
 * (`PortfolioVsSpyChart` via `BacktestChart`) so the Backtest Graph has
 * the same W/1M/3M/6M/YTD/1Y/All presets, $/% toggle and SPY/Target
 * overlays as the account graph — Sergey 2026-05-21. The curve is a
 * deterministic 1-year backtest walk seeded by the bot id, ending at
 * the bot's simulated cumulative return (`twrPct`).
 */
function BotBacktestGraph({ bot }: { bot: Bot }) {
  const curve = useMemo(
    () =>
      syntheticBacktestCurve(
        bot.id,
        (Number.isFinite(bot.twrPct) ? bot.twrPct : 0) * 100,
        365
      ),
    [bot.id, bot.twrPct]
  );
  return (
    <Box sx={{ pb: 2 }}>
      <BacktestChart id={bot.id} curve={curve} />
    </Box>
  );
}

/** Backtest "Simulated Returns" headline metrics. */
function BotSimulatedReturns({ bot }: { bot: Bot }) {
  const rows: { label: string; value: string; tone?: "pos" | "neg" }[] = [
    {
      label: "Cumulative Return",
      value: formatPctSigned(bot.twrPct),
      tone: bot.twrPct >= 0 ? "pos" : "neg"
    },
    {
      label: "Annualized Return",
      value: formatPctSigned(bot.apyPct),
      tone: bot.apyPct >= 0 ? "pos" : "neg"
    },
    { label: "Max Drawdown", value: formatPctSigned(bot.maxDDPct), tone: "neg" },
    {
      label: "Sharpe Ratio",
      value: Number.isFinite(bot.sharpe) ? bot.sharpe.toFixed(2) : "—"
    }
  ];
  return (
    <Box sx={{ px: 2, py: 2 }}>
      {rows.map((r, i) => (
        <Box key={r.label}>
          {i > 0 && <Divider />}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 1.25 }}
          >
            <Typography variant="body1" color="text.secondary">
              {r.label}
            </Typography>
            <Typography
              variant="body1"
              sx={(t) => ({
                fontWeight: 700,
                fontFeatureSettings: '"tnum" 1',
                color:
                  r.tone === "pos"
                    ? t.palette.success.main
                    : r.tone === "neg"
                      ? t.palette.error.main
                      : t.palette.text.primary
              })}
            >
              {r.value}
            </Typography>
          </Stack>
        </Box>
      ))}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        Backtest results are simulated and not reflective of actual
        performance.
      </Typography>
    </Box>
  );
}

/**
 * Backtest vs SPY comparison — a flat side-by-side metric table. SPY
 * figures are a deterministic benchmark mock.
 */
function BotBacktestComparisons({ bot }: { bot: Bot }) {
  // Mock SPY benchmark — fixed reference numbers so the comparison
  // reads consistently across bots. Real benchmarking lands with the
  // backtest engine.
  const spy = { cumulative: 0.088, annualized: 0.121, sharpe: 0.94, maxDD: -0.102 };
  const rows: {
    label: string;
    bot: string;
    spy: string;
    botWins: boolean;
  }[] = [
    {
      label: "Cumulative Return",
      bot: formatPctSigned(bot.twrPct),
      spy: formatPctSigned(spy.cumulative),
      botWins: bot.twrPct >= spy.cumulative
    },
    {
      label: "Annualized Return",
      bot: formatPctSigned(bot.apyPct),
      spy: formatPctSigned(spy.annualized),
      botWins: bot.apyPct >= spy.annualized
    },
    {
      label: "Sharpe Ratio",
      bot: Number.isFinite(bot.sharpe) ? bot.sharpe.toFixed(2) : "—",
      spy: spy.sharpe.toFixed(2),
      botWins: bot.sharpe >= spy.sharpe
    },
    {
      label: "Max Drawdown",
      bot: formatPctSigned(bot.maxDDPct),
      spy: formatPctSigned(spy.maxDD),
      // Less-negative drawdown is better.
      botWins: bot.maxDDPct >= spy.maxDD
    }
  ];
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          columnGap: 2.5,
          alignItems: "center"
        }}
      >
        <Box />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, textAlign: "right", color: "primary.main" }}
        >
          Backtest
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, textAlign: "right", color: "text.secondary" }}
        >
          SPY
        </Typography>
        {rows.map((r) => (
          <Box key={r.label} sx={{ display: "contents" }}>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Divider sx={{ my: 0 }} />
            </Box>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ py: 1.25 }}
            >
              {r.label}
            </Typography>
            <Typography
              variant="body1"
              sx={(t) => ({
                py: 1.25,
                textAlign: "right",
                fontWeight: 700,
                fontFeatureSettings: '"tnum" 1',
                color: r.botWins
                  ? t.palette.success.main
                  : t.palette.text.primary
              })}
            >
              {r.bot}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                py: 1.25,
                textAlign: "right",
                fontFeatureSettings: '"tnum" 1',
                color: "text.secondary"
              }}
            >
              {r.spy}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        SPY figures are a fixed benchmark reference. Backtest results
        are simulated.
      </Typography>
    </Box>
  );
}

// ---- Structure tab --------------------------------------------------------

/**
 * Structure (IF/ELSE rule tree) tab. The visual editor is built with
 * the bot-builder (Phase 5); until then this is a clear placeholder
 * so the tab is navigable and its purpose is obvious.
 */
function BotStructureTab() {
  return (
    <EmptyState
      icon={<AccountTreeOutlined />}
      title="Algo editor"
      body="The visual IF/ELSE rule editor — the rebalancing logic behind the bot — is coming with the bot builder."
    />
  );
}

// ---- Trade Preview tab ----------------------------------------------------

/**
 * Trade Preview — the pre-trade rebalance preview. For an
 * IF/ELSE bot the engine evaluates the rules and produces target
 * weights; this tab shows current → target for each ticker so the
 * user sees what the next rebalance will do before it executes.
 *
 * Increment 2.1 renders a deterministic mock derived from the bot's
 * ticker universe (the real preview engine arrives with the
 * rebalancing runtime). Drafts and archived bots have no live
 * preview — they get an empty state instead.
 */
function BotTradePreviewTab({ bot }: { bot: Bot }) {
  const preview = useMemo(() => buildMockTradePreview(bot), [bot]);

  if (bot.isDraft) {
    return (
      <EmptyState
        icon={<RefreshRounded />}
        title="No preview for a draft"
        body="Finish building and deploy this bot to see its next rebalance preview."
      />
    );
  }
  if (bot.archived) {
    return (
      <EmptyState
        icon={<RefreshRounded />}
        title="Bot is archived"
        body="Archived bots no longer trade, so there's no pending rebalance to preview."
      />
    );
  }

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Next rebalance
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Target weights from the latest rule evaluation
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshRounded sx={{ fontSize: 16 }} />}
        >
          Refresh
        </Button>
      </Stack>

      {preview.map((p, i) => (
        <Box key={p.ticker}>
          {i > 0 && <Divider />}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 1.25 }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, flexShrink: 0, minWidth: 56 }}
            >
              {p.ticker}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontFeatureSettings: '"tnum" 1'
              }}
            >
              {p.current.toFixed(1)}%
            </Typography>
            <Typography variant="body1" sx={{ color: "text.disabled" }}>
              →
            </Typography>
            <Typography
              variant="body1"
              sx={(t) => ({
                fontWeight: 700,
                minWidth: 52,
                textAlign: "right",
                fontFeatureSettings: '"tnum" 1',
                color:
                  p.target > p.current
                    ? t.palette.success.main
                    : p.target < p.current
                      ? t.palette.error.main
                      : t.palette.text.primary
              })}
            >
              {p.target.toFixed(1)}%
            </Typography>
          </Stack>
        </Box>
      ))}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        Preview is indicative — final fills depend on prices at
        execution time.
      </Typography>
    </Box>
  );
}

/**
 * Deterministic mock rebalance preview. Hashes each ticker so the
 * same bot always previews the same current/target split — keeps the
 * demo stable across renders without a real rebalancing engine.
 */
function buildMockTradePreview(
  bot: Bot
): { ticker: string; current: number; target: number }[] {
  const tickers = bot.tickers.length > 0 ? bot.tickers : ["SPY"];
  const hash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  // Raw weights → normalised to 100% for both current and target.
  const cur = tickers.map((t) => 1 + (hash(t + "c") % 100) / 20);
  const tgt = tickers.map((t) => 1 + (hash(t + "t") % 100) / 20);
  const curSum = cur.reduce((a, b) => a + b, 0);
  const tgtSum = tgt.reduce((a, b) => a + b, 0);
  return tickers.map((ticker, i) => ({
    ticker,
    current: (cur[i]! / curSum) * 100,
    target: (tgt[i]! / tgtSum) * 100
  }));
}

// ---- Shared sub-tab strip -------------------------------------------------

/**
 * Compact chip-based secondary tab strip used inside the Live and
 * Backtest tabs. Chips (not MUI Tabs) keep the row light and match
 * the WHB filter-chip aesthetic; scrolls horizontally if the labels
 * don't fit a narrow phone.
 */
function DrawerSubTabs<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ key: T; label: string }>;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ px: 2, pt: 1.5, pb: 0.5, overflowX: "auto" }}
    >
      {options.map((o) => (
        <Chip
          key={o.key}
          label={o.label}
          clickable
          size="small"
          variant={value === o.key ? "filled" : "outlined"}
          onClick={() => onChange(o.key)}
          sx={{
            fontWeight: 600,
            flexShrink: 0,
            ...(value === o.key
              ? {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.light" }
                }
              : {})
          }}
        />
      ))}
    </Stack>
  );
}

// ---- Holdings table (Live + Backtest) -------------------------------------

/**
 * Holdings table — ticker, market value, allocation %. Shared by the
 * Live "Holdings" sub-tab and the Backtest "Holdings" sub-tab; the
 * `simulated` flag only changes the section caption. Weights are a
 * deterministic mock derived from the bot's ticker universe until the
 * real holdings feed is wired.
 */
function BotHoldingsTable({
  bot,
  simulated
}: {
  bot: Bot;
  simulated?: boolean;
}) {
  const holdings = useMemo(() => buildMockHoldings(bot), [bot]);

  if (bot.isDraft) {
    return (
      <EmptyState
        icon={<SmartToyOutlined />}
        title="No holdings"
        body="A draft bot holds no positions until it's built and deployed."
      />
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ pb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "text.secondary" }}
        >
          {simulated ? "Simulated holdings" : "Current holdings"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {holdings.length} line{holdings.length === 1 ? "" : "s"}
        </Typography>
      </Stack>
      {holdings.map((h, i) => (
        <Box key={h.ticker}>
          {i > 0 && <Divider />}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 1 }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, flexShrink: 0, minWidth: 64 }}
            >
              {h.ticker}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontFeatureSettings: '"tnum" 1'
              }}
            >
              {formatUSD(h.marketValue)}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                minWidth: 60,
                textAlign: "right",
                fontFeatureSettings: '"tnum" 1'
              }}
            >
              {h.weightPct.toFixed(2)}%
            </Typography>
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Deterministic mock holdings. Hashes each ticker for a stable
 * weight, normalises to ~100% leaving a small idle-cash slice ($USD),
 * and prices each line off `bot.currentValue`.
 */
function buildMockHoldings(
  bot: Bot
): { ticker: string; weightPct: number; marketValue: number }[] {
  const tickers = bot.tickers.length > 0 ? bot.tickers : ["SPY"];
  const hash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  const raw = tickers.map((t) => 1 + (hash(t + "h") % 100) / 10);
  const sum = raw.reduce((a, b) => a + b, 0);
  const cashPct = 0.31; // small idle-cash remainder
  const investablePct = 100 - cashPct;
  const rows = tickers
    .map((ticker, i) => {
      const weightPct = (raw[i]! / sum) * investablePct;
      return {
        ticker,
        weightPct,
        marketValue: (weightPct / 100) * bot.currentValue
      };
    })
    .sort((a, b) => b.weightPct - a.weightPct);
  return [
    {
      ticker: "$USD",
      weightPct: cashPct,
      marketValue: (cashPct / 100) * bot.currentValue
    },
    ...rows
  ];
}

// ---- History tab ----------------------------------------------------------

/**
 * Live "History" sub-tab — recent trades the bot has executed.
 * Deterministic mock keyed off the bot's tickers until the real
 * per-bot trade feed is wired.
 */
function BotHistoryTab({ bot }: { bot: Bot }) {
  const history = useMemo(() => buildMockHistory(bot), [bot]);

  if (bot.isDraft || history.length === 0) {
    return (
      <EmptyState
        icon={<RefreshRounded />}
        title="No trade history"
        body={
          bot.isDraft
            ? "A draft bot hasn't traded yet."
            : "This bot has no recorded trades."
        }
      />
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {history.map((t, i) => (
        <Box key={i}>
          {i > 0 && <Divider />}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 1.25 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.75} alignItems="baseline">
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {t.ticker}
                </Typography>
                <Typography
                  variant="caption"
                  sx={(th) => ({
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color:
                      t.side === "BUY"
                        ? th.palette.success.main
                        : th.palette.error.main
                  })}
                >
                  {t.side}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatDate(t.date)} · {t.shares} @ {formatUSD(t.price)}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, fontFeatureSettings: '"tnum" 1' }}
            >
              {formatUSD(t.amount)}
            </Typography>
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Deterministic mock trade history — ~8 rows walking back from a
 * fixed recent date, alternating buy/sell across the bot's universe.
 */
function buildMockHistory(bot: Bot): {
  date: string;
  ticker: string;
  side: "BUY" | "SELL";
  shares: number;
  price: number;
  amount: number;
}[] {
  if (bot.tickers.length === 0) return [];
  const hash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  const base = new Date(2026, 4, 13); // fixed so the mock is stable
  const out: {
    date: string;
    ticker: string;
    side: "BUY" | "SELL";
    shares: number;
    price: number;
    amount: number;
  }[] = [];
  for (let i = 0; i < 8; i++) {
    const ticker = bot.tickers[i % bot.tickers.length]!;
    const h = hash(ticker + ":" + i);
    const side: "BUY" | "SELL" = h % 2 === 0 ? "BUY" : "SELL";
    const price = 20 + (h % 4000) / 10;
    const shares = 1 + (h % 60);
    const d = new Date(base);
    d.setDate(d.getDate() - i * 3);
    out.push({
      date: d.toISOString().slice(0, 10),
      ticker,
      side,
      shares,
      price,
      amount: price * shares
    });
  }
  return out;
}
